import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../../data";
import { currentLang, t } from "../../../lib/sprache";
import { fehlerText } from "../../../lib/fehler";
import { useBfcacheRueckkehr } from "../../../lib/bfcache";
import { billingChange, loadSettings } from "./data";
import { preisNettoHinweis } from "../../../model/tarif";

/**
 * Abschluss über PayPal (0173). Vorher die Rechnungsangaben: Stripe stellt für jede PayPal-Zahlung die Rechnung aus (Firma,
 * Adresse, USt-IdNr für Reverse Charge). Danach geht es auf die Freigabeseite von PayPal; zurück kommt man auf Abo & Tarif.
 * Sichtbar nur, wenn der Build PayPal kennt (VITE_PAYPAL=1, gesetzt von tools/staging-deploy.sh, sobald PayPal eingerichtet ist).
 */
export function paypalVerfuegbar(): boolean {
  return (import.meta.env.VITE_PAYPAL as string | undefined) === "1";
}

// Länder für die Rechnungsanschrift; die Namen liefert der Browser in der Sprache der Oberfläche (Intl.DisplayNames)
const LAENDER = ["DE", "AT", "CH", "NL", "BE", "LU", "FR", "IT", "ES", "PL", "DK", "SE", "GB", "US", "AE"];
function landName(code: string): string {
  try {
    return new Intl.DisplayNames([currentLang()], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

export function PaypalAbschluss({ orgId, plan, interval, ohneTestphase, onAbbrechen }: {
  orgId: string; plan: string | null; interval: "month" | "year"; ohneTestphase: boolean; onAbbrechen: () => void;
}) {
  const [f, setF] = useState({ name: "", line1: "", postal_code: "", city: "", country: "DE", vat_id: "" });
  const [falsch, setFalsch] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Inhaber 27.10.2026: Rechnungsangaben aus den Firmendaten vorbefüllen, damit niemand tippt, was die Agentur schon hinterlegt hat;
  // nur leere Felder werden ergänzt (eigene Eingaben bleiben)
  useEffect(() => {
    let aus = false;
    loadSettings(api, orgId).then((s) => {
      if (aus) return;
      setF((alt) => ({
        name: alt.name || s.legal_name || "", line1: alt.line1 || s.street || "", postal_code: alt.postal_code || s.postal_code || "",
        city: alt.city || s.city || "", country: s.country && LAENDER.includes(s.country) && alt.country === "DE" ? s.country : alt.country, vat_id: alt.vat_id || s.vat_id || "",
      }));
    }).catch(() => undefined);
    return () => { aus = true; };
  }, [orgId]);
  // Sofortige Sperre gegen Doppelklick (K-097): „busy“ greift erst nach dem nächsten Zeichnen
  const laeuft = useRef(false);
  // Zurück von PayPal (Seite aus dem bfcache): Knopf wieder freigeben
  useBfcacheRueckkehr(() => { laeuft.current = false; setBusy(false); });
  const feld = (k: keyof typeof f, label: string, opts: { auto?: string } = {}) => (
    <label>
      {label}
      <input value={f[k]} autoComplete={opts.auto} aria-invalid={falsch.includes(k) || undefined} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </label>
  );
  const los = async () => {
    if (!plan || laeuft.current) return;
    laeuft.current = true;
    setBusy(true);
    setFehler(null);
    setFalsch([]);
    try {
      const r = await billingChange(api, orgId, { action: "paypal_checkout", plan, interval, ...(ohneTestphase ? { ohne_testphase: true } : {}), rechnung: f });
      if (!r.url) throw new Error(t("PayPal hat keine Freigabeseite geliefert.", "PayPal did not return an approval page."));
      window.location.assign(r.url);
    } catch (e) {
      const felder = e instanceof ApiError && e.hint?.startsWith("billing:fields:") ? e.hint.slice("billing:fields:".length).split(",").filter(Boolean) : undefined;
      if (felder?.length) setFalsch(felder);
      setFehler(fehlerText(e));
      setBusy(false);
      laeuft.current = false;
    }
  };
  return (
    <div className="panel stack" aria-labelledby="paypal-titel">
      <strong id="paypal-titel">{t("Mit PayPal bezahlen", "Pay with PayPal")}</strong>
      {/* Praxistest P-5 d: die Einschränkungen vor dem Abschluss nennen; Inhaber 27.10.2026: nicht als Abschreckung an der wichtigsten
          Stelle, sondern aufklappbar */}
      <details className="tk-paypal-unterschiede">
        <summary>{t("Unterschiede zur Karte", "Differences to card")}</summary>
        <small>{t("Mit PayPal gibt es keine Zusatzpakete und keine Rabattcodes. Ein Tarifwechsel gilt ab der nächsten Zahlung, und eine Kündigung lässt sich nicht zurücknehmen. Mit Karte über Stripe gilt das nicht.", "With PayPal there are no add-on packages and no discount codes. A plan change applies from the next payment, and a cancellation cannot be undone. With a card via Stripe this does not apply.")}</small>
      </details>
      {/* K-494: derselbe Satz an allen Preisen der App */}
      <small>{t("Für deine Rechnung brauchen wir Firma und Anschrift.", "For your invoice we need company and address.")} {preisNettoHinweis()}</small>
      <div className="tk-felder">
        {feld("name", t("Firma bzw. Name", "Company or name"), { auto: "organization" })}
        {feld("line1", t("Straße und Hausnummer", "Street and number"), { auto: "address-line1" })}
        {feld("postal_code", t("PLZ", "Postcode"), { auto: "postal-code" })}
        {feld("city", t("Ort", "City"), { auto: "address-level2" })}
        <label>
          {t("Land", "Country")}
          <select value={f.country} aria-invalid={falsch.includes("country") || undefined} onChange={(e) => setF({ ...f, country: e.target.value })}>
            {LAENDER.map((k) => <option key={k} value={k}>{landName(k)}</option>)}
          </select>
        </label>
        {/* Inhaber 27.10.2026: kein Pflichtfeld, ohne Zusatz „freiwillig“ */}
        {feld("vat_id", t("USt-IdNr.", "VAT ID"))}
      </div>
      {fehler && <p className="tk-error" role="alert">{fehler}</p>}
      <div className="row">
        <button type="button" className="primary" disabled={!plan || busy} aria-busy={busy} onClick={() => void los()}>
          {busy ? t("PayPal wird geöffnet …", "Opening PayPal …") : ohneTestphase ? t("Weiter zu PayPal (sofort starten)", "Continue to PayPal (start now)") : t("Weiter zu PayPal (Testphase starten)", "Continue to PayPal (start trial)")}
        </button>
        <button type="button" disabled={busy} onClick={onAbbrechen}>{t("Zurück", "Back")}</button>
      </div>
    </div>
  );
}
